import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useMediaActionDetailApi,
  useRequestAutoArticleCreationMutation,
  useUpdateMediaActionMutation
} from '@/common/api/modules/mediaAction'
import { priorityBadgeType, priorityLabel } from '@/common/app/modules/media/articleCreator'
import {
  actionStatusBadgeType,
  actionStatusLabel,
  actionTypeLabel,
  buildActionTargetUrl,
  sourceAnalysisLabel
} from '@/common/app/modules/media/mediaAction'
import { dateTimeStr } from '@/common/form/date'
import { ActionDetail, MediaActionStatus } from '@/common/model/media/mediaAction'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { ActionDetailModalContext } from '@/components/container/modal/automationReview/ActionDetailModalProvider'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import MediaActionStatusDisclosureSelector from '@/components/container/modules/media/action/MediaActionStatusDisclosureSelector'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

// 分類タグ＋値の1行（アクションID・分析実行日時など）
const LabeledValue = ({ label, value }: { label: string; value: string }) => (
  <ItemAlign gapX={4} align='baseline'>
    <Badge size={10} badgeType={BadgeType.SQUARE_TERTIARY}>
      {label}
    </Badge>
    <Text size={14}>{value}</Text>
  </ItemAlign>
)

interface ActionDetailBodyProps {
  detail: ActionDetail
  siteId: number
  onStatusUpdated(): void | Promise<void>
}

// 推奨アクションの詳細本文（アクション概要・推奨元分析・親子アクション）
const ActionDetailBody = ({ detail, siteId, onStatusUpdated }: ActionDetailBodyProps) => {
  const a = detail.action

  return (
    <div className='flex flex-col gap-5'>
      {/* アクション概要 */}
      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap items-center gap-4'>
          <LabeledValue label='アクションID' value={`#${a.id}`} />
          {/* ステータスは一覧行と同じセレクタで切り替える */}
          <MediaActionStatusDisclosureSelector
            siteId={siteId}
            actionId={a.id}
            status={a.status as MediaActionStatus}
            disabled={a.auto_article_creation_requested}
            widthFull={false}
            onUpdated={onStatusUpdated}
          />
        </div>
        <PriorityBar
          priority={a.priority}
          badgeText={actionTypeLabel(a.action_type)}
          title={a.title}
          contentText={a.description || '（説明なし）'}
          subContent={
            (a.target_keywords ?? []).length > 0 && (
              <div className='flex w-70 flex-col gap-1'>
                <Badge size={10} badgeType={BadgeType.SQUARE_TERTIARY}>
                  提案キーワード
                </Badge>
                <ItemAlign gapX={4} gapY={4} align='center' direction='horizon'>
                  {a.target_keywords.map((kw, ki) => (
                    <Badge key={ki} size={10} badgeType={BadgeType.KEYWORD}>
                      {kw}
                    </Badge>
                  ))}
                </ItemAlign>
              </div>
            )
          }
        />
      </div>

      {/* 元の分析の概要 */}
      {detail.source_summary ? (
        <div className='flex flex-col gap-2'>
          <Text size={14} weight='bold'>
            推奨元分析
          </Text>
          <InsideArea color={BgColor.TERTIARY} border>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-wrap items-center gap-4'>
                <LabeledValue
                  label='分析実行日時'
                  value={
                    detail.source_summary.created_at
                      ? dateTimeStr(detail.source_summary.created_at)
                      : '-'
                  }
                />
                <LabeledValue label='履歴ID' value={`#${detail.source_summary.history_id}`} />
              </div>
              <div className='flex items-center gap-3'>
                <Text size={16} weight='bold'>
                  {sourceAnalysisLabel(detail.source_summary.analysis_type)}
                </Text>
                {detail.source_link && (
                  <TextButton link={detail.source_link} newTab>
                    分析結果詳細
                  </TextButton>
                )}
              </div>
              {detail.source_summary.summary && (
                <Text size={12} color={TextColor.SECONDARY}>
                  サマリー: {detail.source_summary.summary}
                </Text>
              )}
            </div>
          </InsideArea>
        </div>
      ) : (
        <Text size={12} color={TextColor.SECONDARY}>
          元の分析履歴が見つかりません（削除された可能性があります）
        </Text>
      )}

      {/* 親アクション（AI分解の元） */}
      {detail.parent && (
        <div className='flex flex-col gap-2'>
          <Text size={14} weight='bold'>
            このアクションの分解元（戦略アクション）
          </Text>
          <InsideArea color={BgColor.SECONDARY} border>
            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  #{detail.parent.id}
                </Text>
                <Badge size={10} badgeType={actionStatusBadgeType(detail.parent.status)}>
                  {actionStatusLabel(detail.parent.status)}
                </Badge>
              </div>
              <Text size={14} weight='bold'>
                {detail.parent.title}
              </Text>
            </div>
          </InsideArea>
        </div>
      )}

      {/* 子アクション（AI分解で生まれたもの） */}
      {detail.children && detail.children.length > 0 && (
        <div className='flex flex-col gap-2'>
          <Text size={14} weight='bold'>
            AI分解で生まれた子アクション ({detail.children.length}件)
          </Text>
          <div className='flex flex-col gap-1.5'>
            {detail.children.map((c) => (
              <InsideArea key={c.id} color={BgColor.SECONDARY} border>
                <div className='flex flex-col gap-1'>
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <Badge size={10} badgeType={BadgeType.EMPHASIS}>
                      {actionTypeLabel(c.action_type)}
                    </Badge>
                    <Badge size={10} badgeType={priorityBadgeType(c.priority)}>
                      {priorityLabel(c.priority)}
                    </Badge>
                    <Text size={10} color={TextColor.SECONDARY}>
                      #{c.id} ・ {actionStatusLabel(c.status)}
                    </Text>
                  </div>
                  <Text size={12}>{c.title}</Text>
                  {(c.target_keywords ?? []).length > 0 && (
                    <div className='flex flex-wrap gap-1'>
                      {c.target_keywords?.map((kw, i) => (
                        <Badge key={i} size={10} badgeType={BadgeType.SECONDARY}>
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </InsideArea>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ActionDetailModal = () => {
  const navigate = useNavigate()
  const { modal, showing, hide } = useContext(ActionDetailModalContext)
  const { addMessage } = useContext(ToastMessageContext)
  const { showModal: showConfirm, hide: hideConfirm } = useContext(ConfirmDialogContext)

  const { data: detail, mutate } = useMediaActionDetailApi(
    showing ? { site_id: modal.siteId, action_id: modal.actionId } : null
  )

  const { trigger: updateStatus } = useUpdateMediaActionMutation({
    onSuccess: async () => {
      await mutate()
      modal.onChanged?.()
    }
  })

  const { trigger: requestAutoArticleCreation, isMutating: requestingAutoCreate } =
    useRequestAutoArticleCreationMutation({
      onSuccess: async () => {
        hideConfirm()
        await mutate()
        modal.onChanged?.()
      }
    })

  const a = detail?.action
  const status = a?.status as MediaActionStatus | undefined
  const targetUrl = a ? buildActionTargetUrl(a, modal.siteId) : null

  // ステータス変更後は詳細を再取得し、呼び出し元（一覧・ロードマップ）も更新する
  const handleStatusUpdated = async () => {
    await mutate()
    modal.onChanged?.()
  }

  const handleAutoCreate = () =>
    a &&
    showConfirm({
      message:
        'この推奨アクションから記事の自動作成を予約します。バックグラウンドで数分以内に処理され、他の操作は完了までできなくなります。よろしいですか？',
      noText: 'キャンセル',
      yesText: '自動記事作成を予約',
      onClickYes: async () => {
        await requestAutoArticleCreation({ site_id: modal.siteId, action_id: a.id })
      }
    })

  // AI記事作成／記事リライト：対象画面へ遷移（提案中の場合は進行中へも遷移する）
  const handleOpenTargetScreen = async () => {
    if (!a) return
    if (status === 'suggested') {
      await updateStatus({ site_id: modal.siteId, action_id: a.id, status: 'in_progress' })
      addMessage({ text: 'ステータスを進行中にしました', type: MessageType.SUCCESS })
    }
    if (targetUrl) {
      navigate(targetUrl)
      hide()
    }
  }

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='推奨アクション詳細' onClickClose={hide} />
      <ModalContent>
        {detail ? (
          <ActionDetailBody
            detail={detail}
            siteId={modal.siteId}
            onStatusUpdated={handleStatusUpdated}
          />
        ) : (
          <PageListLoading small />
        )}
      </ModalContent>
      {a && (
        <ModalFooter>
          {/* ステータス変更（完了・却下）はステータスセレクタで行う */}
          <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
            閉じる
          </Button>
          {a.auto_article_creation_requested ? (
            <Button disabled>自動記事作成実行中</Button>
          ) : (
            <>
              {targetUrl &&
                (a.action_type === 'create_article' || a.action_type === 'rewrite_article') && (
                  <TextButton onClick={handleOpenTargetScreen} arrow>
                    {a.action_type === 'create_article' ? 'AI記事作成' : '記事リライト'}
                  </TextButton>
                )}
              {a.action_type === 'create_article' && (
                <AiButton onClick={handleAutoCreate} disabled={requestingAutoCreate} size='large'>
                  自動記事作成
                </AiButton>
              )}
            </>
          )}
        </ModalFooter>
      )}
    </Modal>
  )
}

export default ActionDetailModal
